import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, Grid } from "@mui/material";
import { BaseButton } from "../../../common/base-button/base-button.jsx";
import { AddCircle } from "iconsax-react";
import DepositRefundOptions from "./deposit-refund-options";
import { t } from "i18next";
import { LocaleContext } from "../../../../routing/LangRouter.js";
import BASE_URL from "../../../../constants/baseUrls.js";
import axios from "axios";
import { useLocation, useNavigate, createSearchParams } from "react-router-dom";
import { numberToText } from "../../../../utils/numberToText.js";
import BaseTextField from "../../../common/base-text-field/base-text-field.jsx";
import AutoCompleteComponent from "../../../common/base-select/autocomplete-component.js.jsx";
import { useForm } from "react-hook-form";

const maskCardNumber = cardNumber => {
    // Mask the card number: show the first 4 and last 4 digits, mask the middle
     const masked = cardNumber.replace(/^(\d{4})\d{8}(\d{4})$/, "$1****$2")
    return `\u200E${masked}`; // Left-to-Right mark
};

// Helper function to map locale to supported languages
const getLanguageFromLocale = locale => {
    const languageMap = {
        fa: "persian",

        en: "english",

        ar: "arabic",
    };

    return languageMap[locale] || "persian"; // Default to Persian if locale not found
};

const DepositDecrement = ({ closeModal }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [cardOptions, setCardOptions] = useState([]);
    const [selectedCard, setSelectedCard] = useState("");
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const { locale } = useContext(LocaleContext);
    const [refundAmount, setRefundAmount] = useState(""); // Raw number without commas
    const [displayValue, setDisplayValue] = useState(""); // Comma-separated formatted value
     const [numberText, setNumberText] = useState(""); // text for the input value
    const { control } = useForm();

    const hasFetchedCards = React.useRef(false);

     // Get the current language for number conversion
    const currentLanguage = getLanguageFromLocale(locale);

    const fetchCards = async () => {
        if (hasFetchedCards.current) return; // Avoid multiple calls
        hasFetchedCards.current = true; // Mark as fetched

        try {
            const accessToken = localStorage.getItem("accessToken");

            const response = await axios.get(`${BASE_URL}/account/user/bank_account/list/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`, // Add auth if required
                },
            });
            // setCardOptions(Array.isArray(response.data.results) ? response.data.results : []);

            const cards = Array.isArray(response.data.results) ? response.data.results : [];

            const formattedCards = cards.map(card => ({
                ...card,
                name: maskCardNumber(card.card_number),
                label: maskCardNumber(card.card_number),
                image: `https://api.atripa.com${card.bank.logo}`,
            }));

            setCardOptions(formattedCards);

            setLoading(false);
        } catch (error) {
            console.error("Error fetching card options:", error);
            setCardOptions([]);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCards(); // Fetch once on component mount

        const handleFocus = () => {
            fetchCards(); // Ensure we fetch again when window regains focus
        };

        // Add event listener for when the window regains focus
        window.addEventListener("focus", handleFocus);

        // Cleanup
        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, []); // Empty dependency array since we only want this effect to run once

    const handleCardChange = selectedValue => {
        console.log("handleCardChange called with:", selectedValue); // Debug log

        if (selectedValue?.id === "add-new-card") {
            navigate({
                pathname: `/${locale}/user-panel/user-account`,
                search: `?${createSearchParams({ prevLocation: location.pathname, tab: 'bankAccountInformation' })}`,
            });
        } else if (selectedValue) {
            console.log("Setting card:", selectedValue); // Debug log
            setSelectedCard(selectedValue.card_number || "");
            setSelectedCardId(selectedValue.id);
            console.log("Set selectedCardId to:", selectedValue.id); // Debug log
        } else {
            // Handle case when selection is cleared
            setSelectedCard("");
            setSelectedCardId(null);
            console.log("Cleared card selection"); // Debug log
        }
    };

    // 2. Create a function to find the selected card object for defaultValue
    const getSelectedCardObject = () => {
        if (!selectedCard) return null;

        const allOptions = [
            ...cardOptions,
            {
                id: "add-new-card",
                name: t("addNewCard"),
                label: t("addNewCard"),
                image: null,
            },
        ];

        return allOptions.find(option => option.card_number === selectedCard) || null;
    };

    const handleConfirmClick = () => {
        if (refundAmount >= 100000) {
            setCurrentStep(2);
        }
    };

    const formatNumber = num => {
        return new Intl.NumberFormat("en-US").format(num);
    };

    const handleAmountChange = event => {
        const inputElement = event.target;
        const rawValue = inputElement.value.replace(/,/g, ""); // Remove commas from the input
        const caretPosition = inputElement.selectionStart; // Get caret position before formatting

        // Allow only numeric input
        if (rawValue === "" || /^\d+$/.test(rawValue)) {
            setRefundAmount(rawValue); // Store raw value for calculations
            const formattedValue = formatNumber(rawValue); // Format with commas
            setDisplayValue(formattedValue); // Update display value

            // Convert raw number to Persian text
            if (rawValue) {
               setNumberText(numberToText(parseInt(rawValue), currentLanguage));
            } else {
                setNumberText(""); // Clear Persian text if input is empty
            }


            
            setError(rawValue && parseInt(rawValue) < 100000); // Validate minimum amount

            // Restore caret position after formatting
            setTimeout(() => {
                const newCaretPosition = caretPosition + (formattedValue.length - rawValue.length);
                inputElement.setSelectionRange(newCaretPosition, newCaretPosition);
            }, 0);
        }
    };

    // Helper function to get text direction based on language
        const getTextDirection = language => {
            return language === "english" ? "ltr" : "rtl";
        };
    
        // Helper function to get text alignment based on language
        const getTextAlign = language => {
            return language === "english" ? "left" : "right";
        };
    
        useEffect(() => {
            if (refundAmount) {
                setDisplayValue(formatNumber(refundAmount));
                // Use the new multilingual function
                setNumberText(numberToText(parseInt(refundAmount), currentLanguage));
            } else {
                setDisplayValue("");
                setNumberText("");
            }
        }, [refundAmount, currentLanguage]); // Added currentLanguage as dependency
    // Responsive styles
    const IS_MOBILE = useMediaQuery("(max-width:600px)");
    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: IS_MOBILE ? "100%" : "400px", // Full width on mobile, 400px max on larger screens
                padding: IS_MOBILE ? "16px" : "24px", // Adjust padding for mobile
                margin: "auto",
                textAlign: "center",
                borderRadius: "8px",
                bgcolor: "background.paper", // Set background color
            }}
        >
            {currentStep === 1 ? (
                <Grid>
                    {/* Title Section */}
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            marginBottom: "16px",
                        }}
                    >
                        {t("depositDecrement")}
                    </Typography>
                    {/* Refundable Credit Section */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px",
                            borderRadius: "8px",
                            marginBottom: "16px",
                            bgcolor: "grey.100", // Light background for better contrast
                        }}
                    >
                        <Typography>{t("minimumRefundCredit")}</Typography>
                        <Typography>{t("minimumRefundAmount")}</Typography>
                    </Box>
                    {/* Card Number Information */}
                    <Grid container justifyContent="space-between" alignItems="center" sx={{ marginBottom: "24px" }}>
                        <Grid item>
                            <Typography sx={{ fontSize: IS_MOBILE ? "12px" : "14px" }}> {t("selectCard")}</Typography>
                        </Grid>
                        <Grid item sx={{ flexGrow: 0.75 }}>
                            <AutoCompleteComponent
                                options={[
                                    ...cardOptions,
                                    {
                                        id: "add-new-card",
                                        name: t("addNewCard"),
                                        label: t("addNewCard"),
                                        image: null
                                    },
                                ]}
                                name="card"
                                defaultValue={getSelectedCardObject()}
                                handleChange={handleCardChange}
                                control={control}
                                loading={loading}
                                // Enable image rendering
                                showImages={true}
                                imageField="image"
                                imageFallback={<AddCircle size={24} />} // Use AddCircle icon as fallback for "Add New Card"
                            />
                        </Grid>
                    </Grid>

                    {/* Refund Amount Section */}
                    <Typography sx={{ pb: "5px", textAlign: "left", fontSize: IS_MOBILE ? "12px" : "14px" }}>{t("refundAmountLabel")}</Typography>
                    <BaseTextField
                        variant="outlined"
                        value={displayValue}
                        setValue={handleAmountChange}
                        fullWidth
                        placeholder={t("refundAmountPlaceholder")}
                        error={error ? t("refundAmountHelperText") : ""}
                    />
                    {/* Persian Text Display */}
                    {numberText && (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 3,
                                color: "text.secondary",
                                textAlign: getTextAlign(currentLanguage),
                                direction: getTextDirection(currentLanguage),
                                fontSize: "0.875rem",
                                fontWeight: 400,
                                opacity: 0.8,
                                 padding: 3,
                            }}
                        >
                            {numberText}
                        </Typography>
                    )}

                    {/* Submit Button */}
                    <div style={{ marginTop: "1.5rem" }}>
                        <BaseButton
                            text={t("continue")}
                            handleClick={handleConfirmClick}
                            color="primary"
                            size="large"
                            radius="radius-10"
                            disabled={!refundAmount || error || !selectedCard}
                        />
                    </div>
                </Grid>
            ) : currentStep === 2 ? (
                <DepositRefundOptions
                    selectedCard={selectedCard}
                    selectedCardId={selectedCardId}
                    refundAmount={refundAmount}
                    closeModal={closeModal}
                />
            ) : null}
        </Box>
    );
};

export default DepositDecrement;

